import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Mic,
  MicOff,
  Play,
  Pause,
  Trash2,
  Upload,
  Link as LinkIcon,
  Image,
  FileText,
  Send,
  Plus,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const NewProjectRequestPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [inspirationLinks, setInspirationLinks] = useState(['']);
  const [images, setImages] = useState([]);
  const [budgetPreference, setBudgetPreference] = useState('');
  const [deadlinePreference, setDeadlinePreference] = useState('');
  
  // Audio state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioTranscript, setAudioTranscript] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Auto-transcribe
        await transcribeAudio(blob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');
      
      const response = await api.post('/briefs/transcribe-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAudioTranscript(response.data.transcription || response.data.transcript || '');
      toast.success('Audio transcrit avec succès !');
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  const deleteAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioTranscript('');
  };

  // Link management
  const addLink = () => {
    setInspirationLinks([...inspirationLinks, '']);
  };

  const updateLink = (index, value) => {
    const newLinks = [...inspirationLinks];
    newLinks[index] = value;
    setInspirationLinks(newLinks);
  };

  const removeLink = (index) => {
    setInspirationLinks(inspirationLinks.filter((_, i) => i !== index));
  };

  // Image upload (mock for now - in real app would upload to storage)
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Veuillez donner un titre à votre projet');
      return;
    }
    
    if (!description.trim() && !audioTranscript) {
      toast.error('Veuillez décrire votre projet (texte ou audio)');
      return;
    }
    
    setSaving(true);
    try {
      const validLinks = inspirationLinks.filter(link => link.trim());
      
      const response = await api.post('/project-requests/', {
        title,
        description: description || audioTranscript,
        audio_transcript: audioTranscript || null,
        audio_url: audioUrl || null,
        inspiration_links: validLinks,
        images: images,
        budget_preference: budgetPreference || null,
        deadline_preference: deadlinePreference || null
      });
      
      toast.success('Demande envoyée ! L\'agence va étudier votre projet.');
      navigate('/my-requests');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="px-4 md:px-8 pb-6 md:pb-8 pt-24">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Nouvelle demande de projet
            </h1>
            <p className="text-slate-500 mt-1">
              Décrivez votre projet et nous vous enverrons une estimation
            </p>
          </div>

          <form onSubmit={handleSubmit} className="max-w-3xl">
            {/* Title */}
            <Card className="card mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cyan-600" />
                  Titre du projet
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Refonte de mon site web, Vidéo promotionnelle..."
                  className="text-lg"
                  required
                />
              </CardContent>
            </Card>

            {/* Audio Brief */}
            <Card className="card mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mic className="w-5 h-5 text-purple-600" />
                  Brief audio
                </CardTitle>
                <CardDescription>
                  Enregistrez un message vocal pour décrire votre projet
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!audioUrl ? (
                  <div className="flex flex-col items-center py-8">
                    <Button
                      type="button"
                      size="lg"
                      onClick={isRecording ? stopRecording : startRecording}
                      className={cn(
                        "w-20 h-20 rounded-full transition-all",
                        isRecording 
                          ? "bg-red-500 hover:bg-red-600 animate-pulse" 
                          : "bg-purple-500 hover:bg-purple-600"
                      )}
                    >
                      {isRecording ? (
                        <MicOff className="w-8 h-8 text-white" />
                      ) : (
                        <Mic className="w-8 h-8 text-white" />
                      )}
                    </Button>
                    <p className="mt-4 text-sm text-slate-500">
                      {isRecording ? 'Cliquez pour arrêter...' : 'Cliquez pour enregistrer'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl">
                      <audio src={audioUrl} controls className="flex-1" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={deleteAudio}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    {isTranscribing && (
                      <div className="flex items-center gap-2 text-purple-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Transcription en cours...</span>
                      </div>
                    )}
                    
                    {audioTranscript && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 mb-2">Transcription :</p>
                        <p className="text-sm text-slate-700">{audioTranscript}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Text Description */}
            <Card className="card mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Description écrite
                </CardTitle>
                <CardDescription>
                  Décrivez votre projet en détail (optionnel si vous avez enregistré un audio)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Décrivez votre projet, vos objectifs, votre cible..."
                  rows={6}
                />
              </CardContent>
            </Card>

            {/* Inspiration Links */}
            <Card className="card mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-emerald-600" />
                  Liens d'inspiration
                </CardTitle>
                <CardDescription>
                  Partagez des exemples qui vous inspirent
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {inspirationLinks.map((link, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={link}
                      onChange={(e) => updateLink(index, e.target.value)}
                      placeholder="https://..."
                      className="flex-1"
                    />
                    {inspirationLinks.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLink(index)}
                        className="text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addLink}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un lien
                </Button>
              </CardContent>
            </Card>

            {/* Images */}
            <Card className="card mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Image className="w-5 h-5 text-orange-600" />
                  Images de référence
                </CardTitle>
                <CardDescription>
                  Uploadez des images pour illustrer votre demande
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={img} 
                        alt={`Reference ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  
                  <label className="w-full h-24 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-cyan-500 hover:bg-cyan-50 transition-colors">
                    <Upload className="w-6 h-6 text-slate-400" />
                    <span className="text-xs text-slate-400 mt-1">Ajouter</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Preferences */}
            <Card className="card mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-600" />
                  Préférences (optionnel)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Budget estimé</Label>
                    <Select value={budgetPreference} onValueChange={setBudgetPreference}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Moins de 1 000€</SelectItem>
                        <SelectItem value="medium">1 000€ - 5 000€</SelectItem>
                        <SelectItem value="high">5 000€ - 10 000€</SelectItem>
                        <SelectItem value="very_high">Plus de 10 000€</SelectItem>
                        <SelectItem value="unknown">À définir</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Délai souhaité</Label>
                    <Select value={deadlinePreference} onValueChange={setDeadlinePreference}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgent">Urgent (moins d'une semaine)</SelectItem>
                        <SelectItem value="short">Court (1-2 semaines)</SelectItem>
                        <SelectItem value="medium">Normal (2-4 semaines)</SelectItem>
                        <SelectItem value="flexible">Flexible (1 mois+)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="gap-2"
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Envoyer la demande
              </Button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default NewProjectRequestPage;
